/*
 * The interface language (P12): one store that React (`useLocale`) and non-React code (`getLocale`) share.
 *
 * - Persisted under the existing preference key `lang` (localStorage `cardnd.lang`, JSON string).
 * - French unless English was chosen: a stored `ar` (Arabic is hidden, Decision 75) or any unknown value reads
 *   as French, and a first visit is French whatever the browser language (Decision 93): crawlers and the legacy
 *   site start in French, and one URL serves one language.
 * - `<html lang>` follows (set by the preferences provider, `applyDocumentLang`).
 * - A page URL can fix the language of one page view (`setPageLocale`, the `/en/…` landing pages, Decision 112):
 *   `getLocale` returns it while the page is shown, and nothing is written to storage. A language the visitor then
 *   picks (`setLocale`) is stored and ends that page language.
 */
import { useSyncExternalStore } from 'react';
import { readStored, writeStored } from '@/services/storage';

export type Locale = 'fr' | 'en';
export const LOCALES: readonly Locale[] = ['fr', 'en'];
export const DEFAULT_LOCALE: Locale = 'fr';

/** Each language in its own language (never translated): the picker and the account menu show these. */
export const LANGUAGE_NAMES: Record<Locale, string> = { fr: 'Français', en: 'English' };

const STORAGE_KEY = 'lang';

/** `en` stays English; anything else (absent, `ar`, garbage) is French. */
export const coerceLocale = (value: unknown): Locale => (value === 'en' ? 'en' : DEFAULT_LOCALE);

let current: Locale | null = null;
let page: Locale | null = null;
const listeners = new Set<() => void>();
const notify = () => {
  for (const listener of listeners) listener();
};

/** The visitor's stored choice (read once from storage, then kept in memory). */
const chosen = (): Locale => {
  if (current === null) current = coerceLocale(readStored<unknown>(STORAGE_KEY, DEFAULT_LOCALE));
  return current;
};

/** The current language: the page's own language if its URL fixes one, else the visitor's choice. */
export function getLocale(): Locale {
  return page ?? chosen();
}

/** Changes the language, persists it and notifies every subscriber (components re-render). */
export function setLocale(locale: Locale | string): void {
  const next = coerceLocale(locale);
  writeStored(STORAGE_KEY, next);
  const before = getLocale();
  current = next;
  if (page !== next) page = null;
  if (getLocale() !== before) notify();
}

/**
 * The language of the page on screen, never stored (`null`: none, the visitor's choice applies). `silent` sets it
 * without notifying, during the page's first render (the components rendered after it read it at once); the call
 * that follows in a layout effect notifies the components rendered before it (the header).
 */
export function setPageLocale(locale: Locale | null, { silent = false } = {}): void {
  page = locale;
  if (!silent) notify();
}

export function subscribeLocale(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** `<html lang>` (screen readers, hyphenation, the browser's translate offer). */
export function applyDocumentLang(locale: Locale): void {
  if (typeof document !== 'undefined') document.documentElement.lang = locale;
}

/** The current language; the component re-renders when it changes. */
export function useLocale(): Locale {
  return useSyncExternalStore(subscribeLocale, getLocale, () => DEFAULT_LOCALE);
}

/** Tests only: forget the in-memory value so the next read comes from storage again. */
export function resetLocaleForTests(): void {
  current = null;
  page = null;
}
